import { Download, Flame, Star, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { EXAM_BY_ID, EXAMS } from "../data/exams";
import { bankStats } from "../data/questions";
import { ExamBadge, ProgressBar, useDocumentTitle } from "../components/ui";
import { ACHIEVEMENTS } from "../lib/achievements";
import { DAY_MS, dayKey, formatDateTime } from "../lib/dates";
import { levelFromXp, liveStreak, parseProgress } from "../lib/progress";
import { examMastery, readiness } from "../lib/stats";
import { progressStore, useProgress } from "../lib/store";
import { displayTitle } from "../lib/session";
import { pick, useT, type L } from "../i18n";

const KIND_LABEL: Record<"practice" | "mock" | "daily" | "review" | "diagnostic", L> = {
  practice: { vi: "Luyện tập", en: "Practice" },
  mock: { vi: "Thi thử", en: "Mock exam" },
  daily: { vi: "Thử thách ngày", en: "Daily challenge" },
  review: { vi: "Ôn tập", en: "Review" },
  diagnostic: { vi: "Kiểm tra trình độ", en: "Placement test" },
};
const WEEKS = 20;

function Heatmap({ activity }: { activity: Record<string, { answered: number }> }) {
  const t = useT();
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  // Bắt đầu từ Chủ nhật của tuần cách đây WEEKS-1 tuần.
  const start = new Date(today.getTime() - ((WEEKS - 1) * 7 + today.getDay()) * DAY_MS);
  const days: { key: string; n: number }[] = [];
  for (let ts = start.getTime(); ts <= today.getTime(); ts += DAY_MS) {
    const key = dayKey(new Date(ts));
    days.push({ key, n: activity[key]?.answered ?? 0 });
  }
  const level = (n: number) => (n === 0 ? 0 : n < 5 ? 1 : n < 15 ? 2 : n < 30 ? 3 : 4);
  const activeDays = days.filter((d) => d.n > 0).length;
  return (
    <>
      <div className="heatmap" role="img" aria-label={t(`Lịch học ${WEEKS} tuần gần nhất: ${activeDays} ngày có hoạt động`, `Activity over the last ${WEEKS} weeks: ${activeDays} active days`)}>
        {days.map((d) => <span key={d.key} data-l={level(d.n)} title={`${d.key.split("-").reverse().join("/")}: ${d.n} ${t("câu", "questions")}`} />)}
      </div>
      <p className="tiny muted mb-0 mt">{t(`${activeDays} ngày học trong ${WEEKS} tuần gần nhất`, `${activeDays} active days in the last ${WEEKS} weeks`)}</p>
    </>
  );
}

export function ProgressPage() {
  const t = useT();
  useDocumentTitle(t("Tiến độ", "Progress"));
  const p = useProgress();
  const lang = p.lang;
  const fileRef = useRef<HTMLInputElement>(null);
  const [msg, setMsg] = useState<{ tone: "ok" | "bad"; text: string } | null>(null);
  const lvl = levelFromXp(p.xp);
  const answered = Object.values(p.questions);
  const totalAnswers = answered.reduce((s, q) => s + q.seen, 0);
  const totalCorrect = answered.reduce((s, q) => s + q.correct, 0);
  const unlocked = ACHIEVEMENTS.filter((a) => p.achievements[a.id]).length;

  const exportData = () => {
    const blob = new Blob([JSON.stringify(p, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `claude-cert-prep-${dayKey()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importData = async (file: File) => {
    try {
      const raw: unknown = JSON.parse(await file.text());
      const parsed = parseProgress(raw);
      if ((raw as { version?: unknown })?.version !== 1) throw new Error(t("File không đúng định dạng sao lưu.", "Not a valid backup file."));
      if (!window.confirm(t("Nhập dữ liệu sẽ GHI ĐÈ toàn bộ tiến độ hiện tại. Tiếp tục?", "Importing will OVERWRITE all current progress. Continue?"))) return;
      progressStore.replace(parsed);
      setMsg({ tone: "ok", text: t("Đã khôi phục tiến độ từ file sao lưu.", "Progress restored from backup.") });
    } catch (e) {
      setMsg({ tone: "bad", text: e instanceof Error ? e.message : t("Không đọc được file.", "Could not read the file.") });
    }
  };

  return (
    <>
      <h1>{t("Tiến độ của bạn", "Your progress")}</h1>

      <div className="grid grid-4">
        <div className="stat"><div className="value"><Star className="star" aria-hidden="true" /> {lvl.level}</div><div className="label">{t("Cấp", "Level")} · {p.xp} XP</div><ProgressBar value={(lvl.into / lvl.need) * 100} label={t("Lên cấp", "Level progress")} /></div>
        <div className="stat"><div className="value"><Flame className="flame" aria-hidden="true" /> {liveStreak(p.streak, dayKey())}</div><div className="label">{t("Chuỗi hiện tại · kỷ lục", "Current streak · best")} {p.streak.best}</div></div>
        <div className="stat"><div className="value">{answered.length}/{bankStats().total}</div><div className="label">{t("Câu đã gặp", "Questions seen")}</div></div>
        <div className="stat"><div className="value">{totalAnswers ? Math.round((totalCorrect / totalAnswers) * 100) : 0}%</div><div className="label">{t(`Tỉ lệ đúng (${totalAnswers} lượt)`, `Accuracy (${totalAnswers} answers)`)}</div></div>
      </div>

      <div className="section-title"><h2>{t("Lịch học", "Activity")}</h2></div>
      <div className="card"><Heatmap activity={p.activity} /></div>

      <div className="section-title"><h2>{t("Sẵn sàng thi", "Exam readiness")}</h2></div>
      <div className="grid grid-2">
        {EXAMS.map((e) => {
          const m = examMastery(e.id, p);
          const r = readiness(e.id, p);
          return (
            <Link key={e.id} to={`/exams/${e.id}`} className="card link row">
              <ExamBadge examId={e.id} />
              <div style={{ flex: 1 }}>
                <div className="row between"><strong>{e.code}</strong><span className="small">{r === null ? "—" : `${r}/1000`}</span></div>
                <ProgressBar value={r === null ? 0 : (r / 1000) * 100} label={`${t("Sẵn sàng", "Readiness")} ${e.code}`} tone={r !== null && r >= 720 ? "ok" : undefined} />
                <div className="tiny muted mt">{m.seen}/{m.total} {t("câu", "questions")} · {m.mastered} {t("thành thạo", "mastered")}</div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="section-title"><h2>{t("Huy hiệu", "Badges")}</h2><span className="small muted">{unlocked}/{ACHIEVEMENTS.length}</span></div>
      <div className="grid grid-3">
        {ACHIEVEMENTS.map((a) => {
          const at = p.achievements[a.id];
          return (
            <div key={a.id} className={`achv${at ? "" : " locked"}`}>
              <span className="icon" aria-hidden="true"><a.icon size={24} /></span>
              <div>
                <div style={{ fontWeight: 600 }}>{pick(a.title, lang)}{!at && <span className="sr-only"> {t("(chưa mở khóa)", "(locked)")}</span>}</div>
                <div className="tiny muted">{pick(a.description, lang)}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="section-title"><h2>{t("Lịch sử", "History")}</h2></div>
      {p.attempts.length ? (
        <div className="card table-wrap">
          <table className="history">
            <thead><tr><th>{t("Thời gian", "Time")}</th><th>{t("Loại", "Type")}</th><th>{t("Nội dung", "Content")}</th><th>{t("Kết quả", "Result")}</th></tr></thead>
            <tbody>
              {p.attempts.slice(0, 30).map((a) => (
                <tr key={a.id}>
                  <td><Link to={`/result/${a.id}`}>{formatDateTime(a.finishedAt, lang)}</Link></td>
                  <td>{pick(KIND_LABEL[a.kind], lang)}</td>
                  <td>{a.examId ? EXAM_BY_ID[a.examId].code : displayTitle(a, lang)}</td>
                  <td>
                    {a.correct}/{a.total}
                    {a.kind === "mock" && <> · <span className={`chip ${a.passed ? "ok" : "bad"}`}>{a.scaled}</span></>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="muted">{t("Chưa có phiên nào hoàn thành.", "No completed sessions yet.")}</p>
      )}

      <div className="section-title"><h2>{t("Dữ liệu", "Data")}</h2></div>
      <div className="card stack">
        <p className="small muted mb-0">
          {t("Tiến độ chỉ lưu trên trình duyệt này (localStorage) — không gửi đi đâu. Sao lưu để chuyển sang máy khác hoặc phòng khi xóa dữ liệu trình duyệt.", "Progress is stored only in this browser (localStorage) — nothing is sent anywhere. Back it up to move devices or in case browser data is cleared.")}
        </p>
        {msg && <div className={`callout ${msg.tone}`} role="status">{msg.text}</div>}
        <div className="row">
          <button type="button" className="btn" onClick={exportData}><Download size={16} aria-hidden="true" /> {t("Xuất file sao lưu", "Export backup")}</button>
          <button type="button" className="btn" onClick={() => fileRef.current?.click()}><Upload size={16} aria-hidden="true" /> {t("Nhập file sao lưu", "Import backup")}</button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importData(f);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            className="btn danger"
            onClick={() => {
              if (window.confirm(t("Xóa TOÀN BỘ tiến độ, XP, huy hiệu? Không thể hoàn tác.", "Delete ALL progress, XP and badges? This cannot be undone."))) {
                progressStore.reset();
                setMsg({ tone: "ok", text: t("Đã xóa tiến độ.", "Progress deleted.") });
              }
            }}
          >
            <Trash2 size={16} aria-hidden="true" /> {t("Xóa tiến độ", "Reset progress")}
          </button>
        </div>
      </div>
    </>
  );
}
